import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { flexRender, Row } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';

import { TableCell, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';

import { cn } from '@/lib/utils';
import type { DataTableRow as DataRow, TableAction, ColumnMeta } from '../types';
import { pinnedCellClasses } from '../lib/pinning';

import { RowActions } from './row-actions';

interface DataTableRowProps<T extends DataRow> {
  row: Row<T>;
  selectable?: boolean;
  actions?: TableAction<T>[];
  pinActions?: boolean;
  hierarchical?: boolean;
  level?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  renderExpandIcon?: (row: T, isExpanded: boolean) => ReactNode;
  rowClassName?: (row: T) => string;
  hasChildren?: boolean;
}

interface ColumnWithMeta {
  hideOnLevel?: number[];
  className?: string | ((row: unknown) => string);
  accessorKey?: string;
}

export const DataTableRow = function DataTableRow<T extends DataRow>({
  row,
  selectable = false,
  actions = [],
  pinActions = false,
  hierarchical = false,
  level = 0,
  isExpanded = false,
  onToggleExpand,
  renderExpandIcon,
  rowClassName,
  hasChildren = false
}: DataTableRowProps<T>) {
  const { t } = useTranslation();
  const rowData = row.original;

  // `group` lets pinned cells reproduce the row hover on their opaque overlay
  const defaultRowClass = cn('group hover:bg-muted/50', rowClassName?.(rowData));

  const hasPinnedLeftColumn = row
    .getVisibleCells()
    .some((cell) => (cell.column.columnDef.meta as ColumnMeta)?.pinned === 'left');

  return (
    <TableRow className={defaultRowClass}>
      {selectable && (
        <TableCell
          className={cn(
            // The checkbox column sits left of a pinned column — it must stick too or it scrolls away underneath
            hasPinnedLeftColumn && pinnedCellClasses('left', { edge: false })
          )}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => {
              row.toggleSelected(!!value);
            }}
            aria-label={t('common.table.selectRow', { id: row.id, defaultValue: `Select row ${row.id}` })}
          />
        </TableCell>
      )}
      {row.getVisibleCells().map((cell, cellIndex) => {
        const column = cell.column.columnDef;
        const columnMeta = column as unknown as ColumnWithMeta;
        const { width, pinned } = (column.meta as ColumnMeta | undefined) ?? {};
        const pinnedClass = pinnedCellClasses(pinned, { afterCheckbox: selectable });

        // Check if column should be hidden at this level
        if (columnMeta.hideOnLevel && level !== undefined && columnMeta.hideOnLevel.includes(level)) {
          return <TableCell key={cell.id} className={cn(pinnedClass)} />;
        }

        const cellContent = column.cell ? flexRender(column.cell, cell.getContext()) : (cell.getValue() as ReactNode);

        const isFirstVisibleCell = cellIndex === 0;
        const shouldShowExpandIcon = hierarchical && isFirstVisibleCell && hasChildren && onToggleExpand;

        return (
          <TableCell
            key={cell.id}
            className={cn(
              // Long content breaks onto the next line (flexible row height) instead
              // of overflowing past the cell edge. Only affects cells that wrap
              // (columns with `whitespace-normal`); inert for nowrap/short cells.
              'wrap-break-word',
              width || '',
              typeof columnMeta.className === 'function' ? columnMeta.className(rowData) : columnMeta.className,
              pinnedClass
            )}>
            {shouldShowExpandIcon ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggleExpand();
                    }
                  }}>
                  {renderExpandIcon ? (
                    renderExpandIcon(rowData, isExpanded)
                  ) : (
                    <ChevronRight
                      className={cn(
                        'w-4 h-4 text-muted-foreground transition-transform duration-200',
                        isExpanded && 'rotate-90'
                      )}
                    />
                  )}
                </button>
                <span>{cellContent}</span>
              </div>
            ) : (
              cellContent
            )}
          </TableCell>
        );
      })}
      {actions.length > 0 && (
        <TableCell className={cn(pinActions && pinnedCellClasses('right'))}>
          <RowActions actions={actions} row={row.original} />
        </TableCell>
      )}
    </TableRow>
  );
};
